/**
 * Upload photographs straight from the finding that says there aren't any.
 *
 * "No usable photo exists in this client's pool" was a dead end: correct,
 * unarguable, and with nowhere to act on it. The photographs usually exist —
 * on somebody's phone, in an email — and the gap was that putting them into
 * the pool meant leaving this screen for another tool.
 *
 * This posts to the SAME governed endpoint the image-assets library uses
 * (POST /client-image-assets/upload), so an upload here is not a
 * landing-page-only artifact: it lands in the private client-images bucket
 * with a client_image_assets row, and content and ads draw from that same
 * pool. Building a second upload path would have fragmented the library that
 * this feature exists to fill.
 */
import { useRef, useState } from 'react';
import { Check, Loader2, Upload, X } from 'lucide-react';

import {
  clientImageAssetsService,
  type ClientImageAssetUploadResponse,
} from '@/services/clientImageAssetsService';

/**
 * Words that identify nothing on their own. Mirrors the spirit of the gate's
 * subject matching; this is a UI HINT ONLY and the gate stays authoritative.
 * A warning here that turns out wrong costs a moment; a missing warning costs
 * an upload that stores fine and is then rejected for reasons nobody sees.
 */
const GENERIC = new Set([
  'service', 'services', 'repair', 'repairs', 'installation', 'install',
  'company', 'contractor', 'residential', 'commercial', 'local', 'and', 'the',
]);

const subjectWords = (service: string) =>
  service
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length >= 4 && !GENERIC.has(w));

/** Same four-character prefix rule the gate uses ("fencing" matches "fence"). */
const describesService = (text: string, service: string) => {
  const words = subjectWords(service);
  if (!words.length) return true;
  const stems = new Set(
    text.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').split(/\s+/).map((w) => w.slice(0, 4))
  );
  return words.every((w) => stems.has(w.slice(0, 4)));
};

export function PhotoUpload({
  tenantId,
  serviceName,
  onUploaded,
}: {
  tenantId: string;
  serviceName: string;
  onUploaded: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  // DELIBERATELY EMPTY. This was prefilled with the service name, which made
  // LP-020 — the check that the label names the service — pass by
  // construction on every upload. The measurement and the thing measured
  // stopped being independent, which is the quotable error again. An empty
  // field the operator must type is more friction and strictly more honest,
  // because the friction is where the judgement happens. (Viktor)
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ClientImageAssetUploadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The failure this warning exists to prevent: a photo uploads successfully,
  // the operator sees "stored", and the page still refuses it because the
  // label does not name the service. Storing and being usable are different
  // things and only one of them is visible.
  const mismatch =
    files.length > 0 && caption.trim().length > 0 && !describesService(caption, serviceName);

  const submit = async () => {
    if (!files.length) return;
    setBusy(true);
    setError(null);
    try {
      const res = await clientImageAssetsService.uploadFiles(tenantId, files, {
        alt_text: caption.trim(),
        description: caption.trim(),
        // Tagged with the service so the pool can find it for this page, and
        // so it is discoverable in the library rather than an untagged blob.
        tags: serviceName,
      });
      setResult(res);
      setFiles([]);
      if (inputRef.current) inputRef.current.value = '';
      if (res.stored > 0) onUploaded();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 rounded border border-border bg-background p-3">
      <p className="text-xs font-medium">Upload photographs now</p>
      <p className="mt-1 text-xs text-teal-dark">
        These go into {tenantId}’s shared image library — the same pool the
        content and ads tools draw from, not just this page.
      </p>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => {
          setFiles(Array.from(e.target.files ?? []));
          setResult(null);
        }}
        className="mt-2 w-full text-xs file:mr-2 file:rounded file:border-0 file:bg-coral
                   file:px-3 file:py-1.5 file:text-white hover:file:bg-coral-hover"
      />

      {files.length > 0 && (
        <>
          <label className="mt-2 block">
            <span className="text-xs font-medium">What do these show?</span>
            <input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder={`e.g. ${serviceName || 'the work'} — finished job, front of property`}
              className="mt-1 w-full rounded border border-border bg-background px-2 py-1.5
                         text-sm text-foreground placeholder:text-muted-foreground"
            />
          </label>

          {/* A HINT, never a gate. Caption text is operator free-text and any
              guard on it can be typed past, so this must not be the thing
              standing between a mislabelled photo and a published page. The
              blockable fact is label_status, which the operator does not
              control — see the note in PhotoUpload's header. */}
          {mismatch && (
            <p className="mt-1 text-xs text-coral-dark">
              This description doesn’t mention “{serviceName}”. The photo will
              be stored, but this page can only use one whose label names the
              service — otherwise a fencing photo could land on a decking page.
            </p>
          )}

          <button
            type="button"
            disabled={busy}
            onClick={submit}
            className="mt-2 inline-flex items-center gap-2 rounded bg-coral px-3 py-1.5
                       text-sm text-white hover:bg-coral-hover disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            Upload {files.length} photo{files.length === 1 ? '' : 's'}
          </button>
        </>
      )}

      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}

      {/* Per FILE, because the endpoint accepts and rejects individually — a
          batch summary would hide which photograph was refused and why. */}
      {result && (
        <div className="mt-2 space-y-1">
          <p className="text-xs font-medium">
            {result.stored} stored{result.rejected > 0 && `, ${result.rejected} rejected`}
          </p>
          {result.results.map((r) => (
            <p key={r.filename} className="flex items-start gap-1.5 text-xs">
              {r.status === 'stored' ? (
                <Check className="mt-0.5 h-3 w-3 shrink-0 text-coral" />
              ) : (
                <X className="mt-0.5 h-3 w-3 shrink-0 text-destructive" />
              )}
              <span className="break-all">
                {r.filename}
                {r.reason && <span className="text-teal-dark"> — {r.reason}</span>}
                {r.source_only && (
                  <span className="text-teal-dark">
                    {' '}— stored, but this format cannot render in a browser, so
                    the page will not use it
                  </span>
                )}
              </span>
            </p>
          ))}
          {result.stored > 0 && (
            <p className="text-xs text-teal-dark">
              Regenerate the page to use them.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
